import { useState, useRef, useEffect } from 'react';

// Yakınlaştırılabilir fotoğraf: fare tekerleği / +− butonları / çift tık ile zoom,
// sürükleyerek kaydırma, dokunmatikte iki parmakla (pinch) zoom ve çift dokunma.
const MIN_SCALE = 1;
const MAX_SCALE = 6;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const ZoomablePhoto = ({ src, alt = 'Fotoğraf', onBackgroundClick }) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  // Yeni fotoğraf açılınca sıfırla
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [src]);

  // Güncel değerler (wheel listener'ı bir kez bağlanıyor, state'i ref'ten okur)
  const viewRef = useRef({ scale: 1, offset: { x: 0, y: 0 } });
  viewRef.current = { scale, offset };

  const pointFromEvent = (clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
  };

  // Belirli bir noktaya (container merkezine göre px) doğru zoom — imlecin altındaki nokta sabit kalır
  const zoomTo = (nextScale, point = { x: 0, y: 0 }) => {
    const { scale: prevScale, offset: prev } = viewRef.current;
    const s = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    setScale(s);
    setOffset(s === 1 ? { x: 0, y: 0 } : {
      x: point.x - (point.x - prev.x) * (s / prevScale),
      y: point.y - (point.y - prev.y) * (s / prevScale)
    });
  };
  const zoomToRef = useRef(zoomTo);
  zoomToRef.current = zoomTo;

  // React'in onWheel'i passive olduğu için preventDefault çalışmaz — native listener
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.2 : 1 / 1.2;
      zoomToRef.current(viewRef.current.scale * factor, pointFromEvent(e.clientX, e.clientY));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onMouseDown = (e) => {
    if (scale === 1) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: offset };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    setOffset({ x: d.origin.x + e.clientX - d.startX, y: d.origin.y + e.clientY - d.startY });
  };
  const endDrag = () => { dragRef.current = null; };

  const onDoubleClick = (e) => {
    if (scale > 1) zoomTo(1);
    else zoomTo(2.5, pointFromEvent(e.clientX, e.clientY));
  };

  const touchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const touchMid = (t) => pointFromEvent((t[0].clientX + t[1].clientX) / 2, (t[0].clientY + t[1].clientY) / 2);
  const tapRef = useRef(null); // çift dokunma algılama: { time, x, y }
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Parmakların ortasındaki nokta pinch boyunca parmakların altında kalır
      pinchRef.current = { dist: touchDist(e.touches), mid: touchMid(e.touches), scale, offset };
      dragRef.current = null;
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      dragRef.current = { startX: t.clientX, startY: t.clientY, origin: offset, moved: false, pan: scale > 1 };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const p = pinchRef.current;
      const s = clamp(p.scale * (touchDist(e.touches) / p.dist), MIN_SCALE, MAX_SCALE);
      const mid = touchMid(e.touches);
      setScale(s);
      setOffset(s === 1 ? { x: 0, y: 0 } : {
        x: mid.x - (p.mid.x - p.offset.x) * (s / p.scale),
        y: mid.y - (p.mid.y - p.offset.y) * (s / p.scale)
      });
    } else if (e.touches.length === 1 && dragRef.current) {
      const d = dragRef.current;
      const dx = e.touches[0].clientX - d.startX;
      const dy = e.touches[0].clientY - d.startY;
      if (Math.abs(dx) + Math.abs(dy) > 8) d.moved = true;
      if (d.pan) setOffset({ x: d.origin.x + dx, y: d.origin.y + dy });
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length > 0) return;
    const d = dragRef.current;
    dragRef.current = null;
    // Dokunma (kaydırma değil) ise çift dokunmayı kontrol et: yakınlaştır / sığdır
    if (!d || d.moved || e.changedTouches.length !== 1) return;
    const t = e.changedTouches[0];
    const now = Date.now();
    const last = tapRef.current;
    if (last && now - last.time < 300 && Math.hypot(t.clientX - last.x, t.clientY - last.y) < 30) {
      tapRef.current = null;
      e.preventDefault(); // ardından gelen sahte click/dblclick tekrar zoom yapmasın
      if (viewRef.current.scale > 1) zoomTo(1);
      else zoomTo(2.5, pointFromEvent(t.clientX, t.clientY));
    } else {
      tapRef.current = { time: now, x: t.clientX, y: t.clientY };
    }
  };

  const btn = { width: '40px', height: '40px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '1.3rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <div style={{ position: 'relative', width: '92vw', height: '86vh' }}>
      <div
        ref={containerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onDoubleClick={onDoubleClick}
        onClick={(e) => { if (onBackgroundClick && scale === 1 && e.target === e.currentTarget) onBackgroundClick(); }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: scale > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'zoom-in', touchAction: 'none', userSelect: 'none' }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px', transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transformOrigin: 'center center', transition: dragRef.current || pinchRef.current ? 'none' : 'transform 0.12s ease-out' }}
        />
      </div>
      <div style={{ position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(0,0,0,0.45)', padding: '6px 12px', borderRadius: '30px' }}>
        <button type="button" style={btn} onClick={() => zoomTo(scale / 1.5)} title="Uzaklaştır">−</button>
        <span style={{ color: '#fff', fontSize: '0.85rem', minWidth: '46px', textAlign: 'center' }}>%{Math.round(scale * 100)}</span>
        <button type="button" style={btn} onClick={() => zoomTo(scale * 1.5)} title="Yakınlaştır">+</button>
        <button type="button" style={{ ...btn, width: 'auto', padding: '0 14px', borderRadius: '20px', fontSize: '0.8rem' }} onClick={() => zoomTo(1)} title="Sığdır">Sığdır</button>
      </div>
    </div>
  );
};

export default ZoomablePhoto;
